import MapboxDraw from '@mapbox/mapbox-gl-draw';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import {useControl} from 'react-map-gl';
import {
  SnapModeDrawStyles,
} from "mapbox-gl-draw-snap-mode";
import CutLineMode from 'mapbox-gl-draw-cut-line-mode';
import MapboxSnap from 'mapbox-gl-snap/dist/esm/MapboxSnap';
import type {MapRef, ControlPosition} from 'react-map-gl';

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition;

  onCreate?: (evt: MapboxDraw.DrawCreateEvent) => void;
  onUpdate?: (evt: MapboxDraw.DrawUpdateEvent) => void;
  onDelete?: (evt: MapboxDraw.DrawDeleteEvent) => void;
  onDrawModeChange?: (evt: MapboxDraw.DrawModeChangeEvent) => void;
  allLayersIds?: string[];
};

export let drawRef: MapboxDraw | null = null;

export default function DrawControl({allLayersIds, ...props}: DrawControlProps) {
  const modes = {
    modes: {
      ...MapboxDraw.modes,
      draw_rectangle: DrawRectangle,
      cut_line: CutLineMode
    },
    styles: SnapModeDrawStyles,
    userProperties: true,
    guides: false,
  };

  //TODO this is dangerous to have global variable => find a proper solution how to use drawer instance in other components if possible.
  drawRef = useControl<MapboxDraw>(
    ({map}: { map: MapRef }) => {
      const drawRef = new MapboxDraw({
        ...props,
        ...modes,
      });

      // fix of "Style is not done loading". probably caused by layer addition after 'style.load' event.
      // therefore, deleted mapLoaded check in MapContainer before children rendering and added here
      // https://github.com/mapbox/mapbox-gl-js/issues/9779#issuecomment-642587419
      map.on('style.load', () => {
        try {
          const mapboxSnap = new MapboxSnap({
            map: map.getMap(),
            drawing: drawRef,
            options: {
              layers: allLayersIds ?? [],
              radius: 15,  // Snap radius in pixels
              rules: ['vertex', 'midpoint', 'edge']
            },
          });
        } catch (e) {
          console.error('Replan.', e);
        }
      })

      return drawRef;
    },
    ({map}: { map: MapRef }) => {
      props.onCreate && map.on('draw.create', props.onCreate);
      props.onUpdate && map.on('draw.update', props.onUpdate);
      props.onDelete && map.on('draw.delete', props.onDelete);
      props.onDrawModeChange && map.on('draw.modechange', props.onDrawModeChange);
    },
    ({map}: { map: MapRef }) => {
      props.onCreate && map.off('draw.create', props.onCreate);
      props.onUpdate && map.off('draw.update', props.onUpdate);
      props.onDelete && map.off('draw.delete', props.onDelete);
      props.onDrawModeChange && map.off('draw.modechange', props.onDrawModeChange);
    },
    {
      position: props.position
    }
  );

  return null;
}